import { IEventLoggingComponentProps, useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { css, StyleDeclarationValue } from 'aphrodite';
import { IErrorMessageComponentProps, IUserSessionComponentProps } from '../../../models/AppState';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import {
	getFeedItemViewModel,
	invalidateDashboardFeed,
	useDashboardFeed,
	useSuppressDashboardItem,
} from '../../../queries';
import { RecentMeetingViewModel } from '../../../viewmodels/AppViewModels';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { RecentMeetingCard } from '../../components/cards/RecentMeetingCard';
import { EmptyState } from '../../components/EmptyState';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { CalendarInCircleGraphic } from '../../components/svgs/graphics/CalendarInCircleGraphic';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps, IErrorMessageComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

const PAGE_SIZE = 100;
const FEED_TYPE = 'Meeting';

export function RecentMeetings({ styles }: IProps) {
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const [listRef] = useAutoAnimate<HTMLDivElement>();
	const { logApiError } = useEventLogging('RecentMeetings');
	const dashboardFeedQuery = useDashboardFeed({
		pageSize: PAGE_SIZE,
		typeOf: FEED_TYPE,
	});
	const suppressDashboardItemMutation = useSuppressDashboardItem({
		onError: error => {
			errorMessages.pushApiError(error);

			logApiError('SuppressRecentMeeting-Error', error);
		},
		onSuccess: () => {
			invalidateDashboardFeed({ pageSize: PAGE_SIZE, typeOf: FEED_TYPE });
		},
	});
	const onSuppressClicked = (x: RecentMeetingViewModel) => () => {
		suppressDashboardItemMutation.mutate({ context: Api.DashboardSuppressContext.RecentMeeting, id: x.id });
	};

	const feedItems = dashboardFeedQuery.data?.values ?? [];

	return (
		<Page styles={[styleSheet.container, ...(styles || [])]}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar currentLocationName='Recent Meetings' />
			</Portal>

			{dashboardFeedQuery.isPending ? <LoadingSpinner type='large' /> : null}
			{dashboardFeedQuery.isSuccess && feedItems.length === 0 ? (
				<EmptyState
					image={<CalendarInCircleGraphic />}
					text={"You don't have any recent meetings"}
					subtext={"More meetings will appear on your dashboard once they've occurred."}
				/>
			) : null}
			{dashboardFeedQuery.isSuccess && feedItems.length > 0 ? (
				<div ref={listRef}>
					{feedItems.map(x => {
						const itemVM = getFeedItemViewModel({ feedItem: x, userSession });
						if (itemVM instanceof RecentMeetingViewModel) {
							return (
								<RecentMeetingCard
									className={css(styleSheet.item)}
									recentMeeting={itemVM}
									key={itemVM.id}
									onSuppressClicked={onSuppressClicked(itemVM)}
								/>
							);
						}
					})}
				</div>
			) : null}
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</Page>
	);
}
